import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoomGrid, CreateRoomModal } from '../components/rooms';
import { HomeHeader } from '../components/home/HomeHeader';
import { useAuthStore } from '../stores/authStore';
import { useRoomStore } from '../stores/roomStore';
import { useHeadsetDevices } from '../hooks/useHeadsetDevices';
import type { PersistentRoom, RoomSceneId } from '../types/room';

/**
 * Home page with persistent rooms and quick join.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { rooms, isLoading, loadUserRooms, createRoom } = useRoomStore();
  const devices = useHeadsetDevices(user?.uid);
  const showPairingIndicator = devices.length === 0;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      loadUserRooms(user.uid, user.email);
    }
  }, [user?.uid, user?.email, loadUserRooms]);

  const handleCreateRoom = async (name: string, sceneId: RoomSceneId, imageFile?: File) => {
    if (!user) return;

    setIsCreatingRoom(true);
    try {
      const room = await createRoom(
        { name, sceneId, imageFile },
        { uid: user.uid, email: user.email, displayName: user.displayName }
      );
      setIsCreateModalOpen(false);
      navigate(`/room/${room.id}`);
    } finally {
      setIsCreatingRoom(false);
    }
  };

  const handleEnterRoom = (room: PersistentRoom) => {
    navigate(`/room/${room.id}`);
  };

  const handleRoomSettings = (room: PersistentRoom) => {
    navigate(`/room/${room.id}/settings`);
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <HomeHeader
        displayName={user?.displayName || ''}
        avatarUrl={user?.avatarUrl}
        showPairingIndicator={showPairingIndicator}
        onSettings={() => navigate('/settings')}
        onPairing={() => navigate('/settings')}
        onLogout={logout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <section className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold">Your Rooms</h1>
          </div>

          <RoomGrid
            rooms={rooms}
            onEnterRoom={handleEnterRoom}
            onRoomSettings={handleRoomSettings}
            onCreateRoom={() => setIsCreateModalOpen(true)}
            isLoading={isLoading}
          />
        </section>
      </main>

      <CreateRoomModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateRoom}
        isLoading={isCreatingRoom}
      />
    </div>
  );
}
